'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

interface BlogPost {
  title: string;
  slug: string;
  date: string;
  category: string;
  excerpt: string;
  imageUrl: string;
}

export default function BlogListing() {
  const [particles, setParticles] = useState<Array<{ x: number; y: number }>>([]);

  useEffect(() => {
    setParticles(
      [...Array(50)].map(() => ({
        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
        y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000)
      }))
    );
  }, []);

  const blogPosts: BlogPost[] = [
    {
      title: 'The Pros and Cons of Free Hosting: Why You Get What You Pay For',
      slug: 'the-pros-and-cons-of-free-hosting-services-why-you-get-what-you-pay-for',
      date: 'December 10, 2024',
      category: 'Web Hosting',
      excerpt: 'Explore the advantages and disadvantages of free hosting services, helping you make an informed decision about your website\'s future.',
      imageUrl: '/assets/blog-images/free-hosting-comparison.svg'
    },
    {
      title: '10 Questions to Ask Before Choosing a Hosting Service',
      slug: '10-questions-to-ask-before-choosing-a-hosting-service-for-your-website',
      date: 'May 9, 2023',
      category: 'Web Hosting',
      excerpt: 'Essential questions to ask when selecting a hosting provider, ensuring you make the right choice for your website needs.',
      imageUrl: '/assets/blog-images/hosting-questions.svg'
    },
    {
      title: 'Why Security Should Be a Top Priority When Choosing a Hosting Service',
      slug: 'why-security-should-be-a-top-priority-when-choosing-a-hosting-service',
      date: 'November 28, 2024',
      category: 'Security',
      excerpt: 'Learn why cybersecurity is crucial when selecting a hosting provider and what security features to look for.',
      imageUrl: '/assets/blog-images/hosting-security.svg'
    },
    {
      title: 'The Ultimate Guide to Choosing the Perfect Domain Name',
      slug: 'the-ultimate-guide-to-choosing-the-perfect-domain-name',
      date: 'December 5, 2024',
      category: 'Domain Names',
      excerpt: 'Learn the essential tips and best practices for selecting a memorable and effective domain name for your website.',
      imageUrl: '/assets/blog-images/domain-name-guide.svg'
    },
    {
      title: 'Cloud Hosting vs Traditional Hosting: Making the Right Choice',
      slug: 'cloud-hosting-vs-traditional-hosting-making-the-right-choice',
      date: 'December 1, 2024',
      category: 'Web Hosting',
      excerpt: 'Compare the benefits and drawbacks of cloud hosting and traditional hosting solutions to find the best fit for your needs.',
      imageUrl: '/assets/blog-images/cloud-vs-traditional.svg'
    },
    {
      title: 'From Shared to Dedicated Hosting: Which Service is Right for You and Why',
      slug: 'from-shared-to-dedicated-hosting-which-service-is-right-for-you-and-why',
      date: 'November 15, 2024',
      category: 'Web Hosting',
      excerpt: 'A comprehensive guide to help you choose between shared, VPS, and dedicated hosting based on your specific website needs.',
      imageUrl: '/assets/blog-images/hosting-types-comparison.svg'
    },
    {
      title: 'How Our Hosting Service Can Improve Your Website\'s Speed and Performance',
      slug: 'how-our-hosting-service-can-improve-your-websites-speed-and-performance',
      date: 'November 10, 2024',
      category: 'Performance',
      excerpt: 'Discover how 1 Dollar Cloud\'s hosting solutions can significantly enhance your website\'s loading times and overall performance.',
      imageUrl: '/assets/blog-images/hosting-performance.svg'
    },
    {
      title: 'The Cheapest Shared Hosting in the Market: A Comprehensive Comparison',
      slug: 'the-cheapest-shared-hosting-in-the-market-a-comprehensive-comparison',
      date: 'November 5, 2024',
      category: 'Web Hosting',
      excerpt: 'An in-depth analysis of affordable shared hosting options, with a focus on finding the best value for your money.',
      imageUrl: '/assets/blog-images/shared-hosting-comparison.svg'
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden -mt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
        <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        
        {/* Animated glowing orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-primary-500/30 to-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        {/* Binary data particles */}
        <div className="absolute inset-0 overflow-hidden">
          {particles.map((particle, i) => (
            <motion.div
              key={i}
              className="absolute text-white/10 font-mono text-sm"
              initial={{
                x: particle.x,
                y: particle.y,
                opacity: 0
              }}
              animate={{
                y: [null, -100],
                opacity: [0, 1, 0],
                x: [null, particle.x + (Math.random() * 100 - 50)]
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "linear"
              }}
            >
              {Math.random() > 0.5 ? '1' : '0'}
            </motion.div>
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative mt-24">
          <div className="text-center">
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Explore Our <span className="text-transparent bg-clip-text bg-gradient-rainbow">Blog Articles</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-slate-300 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Discover insights about web hosting, security, and performance optimization
            </motion.p>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {blogPosts.map((post, index) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={`/${post.slug}`}>
                  <div className="bg-slate-800/50 rounded-xl p-12 h-full hover:bg-slate-800/70 transition-colors duration-300 cursor-pointer">
                    <div className="flex flex-col h-full">
                      <img
                        src={post.imageUrl}
                        alt={post.title}
                        className="w-full h-56 object-contain mb-4 rounded-md min-h-[14rem]"
                      />
                      <div className="flex flex-col flex-grow">
                        <div>
                          <p className="text-slate-400 mb-2">{post.category}</p>
                          <h2 className="text-2xl font-bold text-white mb-4">{post.title}</h2>
                          <p className="text-slate-300 mb-6">{post.excerpt}</p>
                        </div>
                        <div className="mt-auto">
                          <p className="text-slate-400">Published on {post.date}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}